/** Presentation bridge. No ratio, weighting, plausibility or quarter arithmetic. */
type Row = Record<string, unknown>;
export interface ServerPeriods {
  schema_version: number;
  monthly_rows: Row[];
  quarterly_variants: Record<string, Row>;
  build: { build_run_id: string; rule_version: string };
  quality?: Record<string, Record<string, { hints?: string[] }>>;
  rolling?: Record<string, Record<string, { value: number | null; monthsUsed: number }>>;
}
const binding = Symbol("server period contract");
type Bound = Row & { [binding]?: ServerPeriods };
/** Admin-only development candidate inspection, without changing the pointer. */
const candidateParam = import.meta.env.DEV && typeof window !== "undefined"
  ? new URLSearchParams(window.location.search).get("period_candidate") : null;
export const serverPeriodCandidateRoot = candidateParam && /^[0-9a-f]{64}$/.test(candidateParam)
  ? candidateParam : null;
export const serverPeriodsEnabled = import.meta.env.VITE_SERVER_COMPUTED_PERIODS === "true"
  || serverPeriodCandidateRoot !== null;

export function bindServerPeriods<T>(input: T): T {
  if (!serverPeriodsEnabled) return input;
  const core = input as T & {
    server_computed_periods?: ServerPeriods;
    herd_series?: Row[];
    latest_kpis?: Row;
  };
  const contract = core.server_computed_periods;
  if (!contract || contract.schema_version !== 1) {
    throw new Error("Der gewählte Datenstand enthält keine serverseitig berechneten Perioden.");
  }
  const monthly = contract.monthly_rows.map(row => {
    const bound = {
      ...row, _server_quality: contract.quality?.[String(row.month_date).slice(0, 7)],
    } as Bound;
    Object.defineProperty(bound, binding, { value: contract, enumerable: true });
    return bound;
  });
  return {
    ...core,
    herd_series: monthly,
    latest_kpis: monthly[monthly.length - 1] ?? null,
  };
}

export function serverQuarters(rows: Row[]): Row[] | null {
  if (rows.length === 0) return null;
  if (rows.every(row => row._server_computed && row.is_quarterly_input)) return rows;
  const contract = (rows[0] as Bound)[binding];
  if (!contract) {
    if (rows.some(r => (r as Bound)[binding])) throw new Error("Gemischte Periodenquellen");
    return null;
  }
  if (rows.some(r => (r as Bound)[binding] !== contract)) throw new Error("Gemischte Periodenquellen");
  // The server prepares all valid subsets of a quarter, including date-filtered
  // edges. JavaScript only groups identifiers and looks up the ready row.
  const quarters = new Map<string, string[]>();
  for (const row of rows) {
    const month = String(row.month_date).slice(0, 7);
    const entry = Object.entries(contract.quarterly_variants).find(
      ([key]) => key.split(",").length === 1 && key === month,
    );
    if (!entry) throw new Error("Monat fehlt im serverseitigen Periodenvertrag");
    const label = String(entry[1].month);
    quarters.set(label, [...(quarters.get(label) ?? []), month]);
  }
  return [...quarters.values()].map(months => {
    const result = contract.quarterly_variants[[...new Set(months)].sort().join(",")];
    if (!result) throw new Error("Quartal fehlt im serverseitigen Periodenvertrag");
    return { ...result };
  });
}

export function serverRolling(rows: Row[], key: string): { value: number | null; monthsUsed: number } | null {
  const contract = (rows[0] as Bound | undefined)?.[binding];
  if (!contract) return null;
  if (rows.some(r => (r as Bound)[binding] !== contract)) throw new Error("Gemischte Periodenquellen");
  const month = String(rows[rows.length - 1].month_date).slice(0, 7);
  return contract.rolling?.[key]?.[month] ?? { value: null, monthsUsed: 0 };
}
